import { useState, useEffect } from "react";
import axios from "axios";

const PLATFORMS = [
  { id: "facebook", label: "Facebook", color: "#1877F2", icon: "f" },
  { id: "linkedin", label: "LinkedIn", color: "#0A66C2", icon: "in" },
];

interface SocialPost {
  id: number;
  content: string;
  platforms: string[];
  status: string;
  scheduled_at: string | null;
  published_at: string | null;
  image_url: string | null;
  facebook_post_id: string | null;
  linkedin_post_id: string | null;
  twitter_post_id: string | null;
  error_message: string | null;
  created_at: string;
}

export default function SocialMediaAdmin() {
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [composing, setComposing] = useState(false);
  const [publishing, setPublishing] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null);
  const [content, setContent] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(["facebook", "linkedin"]);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const API = process.env.REACT_APP_API_URL || "https://quotebid-backend-production.up.railway.app";

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    const userData = user ? JSON.parse(user) : null;
    if (!token || !userData || (!userData.is_admin && userData.id !== 110 && userData.user_type !== "admin")) {
      window.location.href = "/login";
    } else {
      setAuthorized(true);
    }
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/api/social/posts`, getAuthHeaders());
      setPosts(res.data.posts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authorized) return;
    fetchPosts();
    const interval = setInterval(fetchPosts, 30000);
    return () => clearInterval(interval);
  }, [authorized]);

  const togglePlatform = (id: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const resetForm = () => {
    setContent("");
    setSelectedPlatforms(["facebook", "linkedin"]);
    setScheduleMode(false);
    setScheduledAt("");
    setImageUrl("");
    setComposing(false);
    setEditingPost(null);
    setSuccessMsg("");
    setErrorMsg("");
  };

  const handleSave = async () => {
    if (!content.trim()) return setErrorMsg("Please enter post content.");
    if (selectedPlatforms.length === 0) return setErrorMsg("Select at least one platform.");
    if (scheduleMode && !scheduledAt) return setErrorMsg("Please select a scheduled time.");
    setSaving(true);
    setErrorMsg("");
    try {
      if (editingPost) {
        await axios.put(`${API}/api/social/posts/${editingPost.id}`, {
          content, platforms: selectedPlatforms,
          scheduled_at: scheduleMode ? scheduledAt : null,
          image_url: imageUrl || null,
        }, getAuthHeaders());
        setSuccessMsg("Post updated!");
      } else {
        await axios.post(`${API}/api/social/posts`, {
          content, platforms: selectedPlatforms,
          scheduled_at: scheduleMode ? scheduledAt : null,
          image_url: imageUrl || null,
        }, getAuthHeaders());
        setSuccessMsg(scheduleMode ? "Post scheduled!" : "Post saved as draft!");
      }
      fetchPosts();
      setTimeout(resetForm, 1500);
    } catch (e: any) {
      setErrorMsg(e.response?.data?.error || "Failed to save post.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = async (postId: number) => {
    setPublishing(postId);
    try {
      const res = await axios.post(`${API}/api/social/posts/${postId}/publish`, {}, getAuthHeaders());
      if (res.data.errors?.length > 0) {
        window.alert(`Published with errors:\n${res.data.errors.join("\n")}`);
      }
      fetchPosts();
    } catch (e: any) {
      window.alert(e.response?.data?.error || "Failed to publish");
    } finally {
      setPublishing(null);
    }
  };

  const handleDelete = async (postId: number) => {
    if (!window.confirm("Delete this post?")) return;
    setDeleting(postId);
    try {
      await axios.delete(`${API}/api/social/posts/${postId}`, getAuthHeaders());
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (e: any) {
      window.alert(e.response?.data?.error || "Failed to delete");
    } finally {
      setDeleting(null);
    }
  };

  const handleEdit = (post: SocialPost) => {
    setEditingPost(post);
    setContent(post.content);
    setSelectedPlatforms(post.platforms.filter(p => p !== "twitter"));
    setScheduleMode(!!post.scheduled_at);
    setScheduledAt(post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : "");
    setImageUrl(post.image_url || "");
    setComposing(true);
  };

  const charCount = content.length;

  const stats = {
    total: posts.length,
    scheduled: posts.filter(p => p.status === "scheduled").length,
    published: posts.filter(p => p.status === "published").length,
    failed: posts.filter(p => p.status === "failed").length,
  };

  if (!authorized) return null;

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f9fafb", fontFamily: "sans-serif" }}>
      <div style={{ backgroundColor: "white", borderBottom: "1px solid #e5e7eb", padding: "16px 24px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Social Media Manager</h1>
            <p style={{ fontSize: 14, color: "#6b7280", margin: "4px 0 0" }}>Schedule and publish posts across Facebook and LinkedIn</p>
          </div>
          <button onClick={() => setComposing(true)} style={{ backgroundColor: "#2563eb", color: "white", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>
            + New Post
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
          {[
            { label: "Total Posts", value: stats.total, color: "#111827" },
            { label: "Scheduled", value: stats.scheduled, color: "#2563eb" },
            { label: "Published", value: stats.published, color: "#16a34a" },
            { label: "Failed", value: stats.failed, color: "#dc2626" },
          ].map(stat => (
            <div key={stat.label} style={{ backgroundColor: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 16 }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {composing && (
          <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
            <div style={{ backgroundColor: "white", borderRadius: 16, boxShadow: "0 25px 50px rgba(0,0,0,0.25)", width: "100%", maxWidth: 600, maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ padding: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                  <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{editingPost ? "Edit Post" : "Compose Post"}</h2>
                  <button onClick={resetForm} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#9ca3af" }}>x</button>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 14, fontWeight: 500, display: "block", marginBottom: 8 }}>Publish to</label>
                  <div style={{ display: "flex", gap: 12 }}>
                    {PLATFORMS.map(p => (
                      <button key={p.id} onClick={() => togglePlatform(p.id)} style={{ padding: "8px 16px", borderRadius: 8, border: selectedPlatforms.includes(p.id) ? "2px solid #3b82f6" : "2px solid #e5e7eb", backgroundColor: selectedPlatforms.includes(p.id) ? "#eff6ff" : "white", color: selectedPlatforms.includes(p.id) ? "#1d4ed8" : "#6b7280", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                        {p.icon} {p.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 14, fontWeight: 500, display: "block", marginBottom: 8 }}>Content</label>
                  <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="What do you want to share?" rows={6} style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 12, padding: "12px 16px", fontSize: 14, resize: "none", boxSizing: "border-box" }} />
                  <div style={{ textAlign: "right", fontSize: 13, color: "#9ca3af", marginTop: 4 }}>{charCount} characters</div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 14, fontWeight: 500, display: "block", marginBottom: 8 }}>Image URL (optional)</label>
                  <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 12, padding: "12px 16px", fontSize: 14, boxSizing: "border-box" }} />
                </div>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }} onClick={() => setScheduleMode(!scheduleMode)}>
                    <div style={{ width: 48, height: 24, borderRadius: 12, backgroundColor: scheduleMode ? "#2563eb" : "#d1d5db", position: "relative", transition: "background 0.2s" }}>
                      <div style={{ position: "absolute", top: 2, left: scheduleMode ? 26 : 2, width: 20, height: 20, borderRadius: "50%", backgroundColor: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>Schedule for later</span>
                  </div>
                  {scheduleMode && (
                    <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} min={new Date().toISOString().slice(0, 16)} style={{ marginTop: 12, width: "100%", border: "1px solid #d1d5db", borderRadius: 12, padding: "12px 16px", fontSize: 14, boxSizing: "border-box" }} />
                  )}
                </div>
                {errorMsg && <div style={{ marginBottom: 16, color: "#dc2626", fontSize: 14, backgroundColor: "#fef2f2", padding: 12, borderRadius: 8 }}>{errorMsg}</div>}
                {successMsg && <div style={{ marginBottom: 16, color: "#16a34a", fontSize: 14, backgroundColor: "#f0fdf4", padding: 12, borderRadius: 8 }}>{successMsg}</div>}
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={resetForm} style={{ flex: 1, border: "1px solid #d1d5db", backgroundColor: "white", borderRadius: 12, padding: "12px", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Cancel</button>
                  <button onClick={handleSave} disabled={saving} style={{ flex: 1, backgroundColor: "#2563eb", color: "white", border: "none", borderRadius: 12, padding: "12px", fontWeight: 600, cursor: "pointer", fontSize: 14, opacity: saving ? 0.5 : 1 }}>
                    {saving ? "Saving..." : scheduleMode ? "Schedule Post" : "Save Draft"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ backgroundColor: "white", borderRadius: 12, border: "1px solid #e5e7eb" }}>
          <div style={{ padding: 16, borderBottom: "1px solid #f3f4f6" }}>
            <h2 style={{ margin: 0, fontWeight: 600 }}>All Posts</h2>
          </div>
          {loading ? (
            <div style={{ padding: 48, textAlign: "center", color: "#9ca3af" }}>Loading...</div>
          ) : posts.length === 0 ? (
            <div style={{ padding: 48, textAlign: "center" }}>
              <p style={{ color: "#6b7280" }}>No posts yet. Create your first post!</p>
            </div>
          ) : (
            posts.map(post => (
              <div key={post.id} style={{ padding: 16, borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 999, backgroundColor: post.status === "published" ? "#dcfce7" : post.status === "scheduled" ? "#dbeafe" : post.status === "failed" ? "#fee2e2" : "#f3f4f6", color: post.status === "published" ? "#16a34a" : post.status === "scheduled" ? "#1d4ed8" : post.status === "failed" ? "#dc2626" : "#6b7280" }}>
                      {post.status}
                    </span>
                    {post.platforms.filter(p => p !== "twitter").map(p => <span key={p} style={{ fontSize: 12, backgroundColor: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: 999 }}>{p}</span>)}
                    {post.published_at && <span style={{ fontSize: 12, color: "#9ca3af" }}>Published {new Date(post.published_at).toLocaleString()}</span>}
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: "#1f2937", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{post.content.replace(/&quot;/g, '"')}</p>
                  {post.error_message && <p style={{ margin: "4px 0 0", fontSize: 12, color: "#dc2626" }}>{post.error_message}</p>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {(post.status === "draft" || post.status === "scheduled" || post.status === "failed") && (
                    <button onClick={() => handlePublishNow(post.id)} disabled={publishing === post.id} style={{ fontSize: 12, backgroundColor: "#16a34a", color: "white", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", opacity: publishing === post.id ? 0.5 : 1 }}>
                      {publishing === post.id ? "Publishing..." : "Publish Now"}
                    </button>
                  )}
                  {(post.status === "draft" || post.status === "scheduled") && (
                    <button onClick={() => handleEdit(post)} style={{ fontSize: 12, border: "1px solid #d1d5db", backgroundColor: "white", borderRadius: 8, padding: "6px 12px", cursor: "pointer" }}>Edit</button>
                  )}
                  <button
                    onClick={() => handleDelete(post.id)}
                    disabled={deleting === post.id}
                    style={{ fontSize: 12, color: "white", backgroundColor: "#dc2626", border: "none", borderRadius: 8, padding: "6px 12px", cursor: deleting === post.id ? "not-allowed" : "pointer", opacity: deleting === post.id ? 0.5 : 1 }}
                  >
                    {deleting === post.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}